/* eslint-disable */
import { jsPDF } from "jspdf";
import { sanitizeFilename } from "utils/shareReport";

const MARGIN_X = 32;
const MARGIN_TOP = 30;
const MARGIN_BOTTOM = 34;
const INK = [29, 36, 46];
const MUTED = [72, 80, 90];
const RULE = [192, 198, 205];
const NEGATIVE_TOTAL = [190, 32, 45];

const a4TotalValue = (value, amount) => {
  const text = String(value ?? "-");
  if (!(amount < 0)) return text;
  return text.replace(/^\(-(.+)\)$/, "-$1").replace(/^\$\s*-/, "-$ ");
};

const setText = (doc, size, bold = false, color = INK) => {
  doc.setFont("helvetica", bold ? "bold" : "normal");
  doc.setFontSize(size);
  doc.setTextColor(...color);
};

const lineItem = (text, { size = 9.5, bold = false, color = INK, indent = 0, gap = 0 } = {}) => ({
  height: size * 1.42 + gap,
  draw(doc, x, y) {
    setText(doc, size, bold, color);
    doc.text(String(text), x + indent, y + size);
  },
});

const wrappedItems = (doc, text, width, options = {}) => {
  setText(doc, options.size ?? 9.5, options.bold ?? false);
  return doc.splitTextToSize(String(text ?? ""), width).map((line) => lineItem(line, options));
};

const spacer = (height) => ({ height, draw() {} });
const ruleItem = (width, indent = 0) => ({
  height: 8,
  draw(doc, x, y) {
    doc.setDrawColor(...RULE);
    doc.setLineWidth(0.45);
    doc.line(x + indent, y + 3, x + width, y + 3);
  },
});
const itemHeight = (items) => items.reduce((sum, item) => sum + item.height, 0);

const machineItems = (doc, machine, width) => {
  const items = [];
  const values = machine.valueLines || [];
  const main = values.find((line) => line.label === "Total" || line.label === "Alquiler");
  setText(doc, 10.8, true);
  const titleLines = doc.splitTextToSize(machine.title || "-", width);
  titleLines.forEach((line) => {
    items.push({
      height: 15,
      draw(pdf, x, y) {
        setText(pdf, 10.8, true);
        pdf.text(line, x, y + 10.8);
      },
    });
  });
  if (machine.status) items.push(lineItem(machine.status, { size: 9.5, bold: true }));

  if (machine.rows?.length) {
    items.push(spacer(3));
    items.push({
      height: 14,
      draw(pdf, x, y) {
        setText(pdf, 9.5, true, MUTED);
        pdf.text("Entrada", x + width * 0.57, y + 9.5, { align: "right" });
        pdf.text("Salida", x + width, y + 9.5, { align: "right" });
      },
    });
    machine.rows.forEach((row) => {
      const label = String(row.label ?? "");
      const entry = String(row.inValue ?? "-");
      const exit = String(row.outValue ?? "-");
      setText(doc, 9.7);
      const fits =
        doc.getTextWidth(label) <= width * 0.23 &&
        doc.getTextWidth(entry) <= width * 0.31 &&
        doc.getTextWidth(exit) <= width * 0.31;
      if (fits) {
        items.push({
          height: 14.2,
          draw(pdf, x, y) {
            setText(pdf, 9.7);
            pdf.text(label, x, y + 9.7);
            pdf.text(entry, x + width * 0.57, y + 9.7, { align: "right" });
            pdf.text(exit, x + width, y + 9.7, { align: "right" });
          },
        });
      } else {
        items.push(lineItem(label, { size: 9.7, bold: true }));
        items.push(
          ...wrappedItems(doc, `Entrada ${entry}  |  Salida ${exit}`, width, {
            size: 9.7,
            indent: 10,
          })
        );
      }
    });
  }

  values
    .filter((line) => line !== main)
    .forEach((line) => {
      items.push(...wrappedItems(doc, `${line.label} ${line.value ?? "-"}`, width, { size: 9.6 }));
    });
  (machine.resetLines || []).forEach((line) => {
    items.push(
      ...wrappedItems(doc, `${line.label} ${line.value ?? "-"}`, width, {
        size: 9.2,
        color: MUTED,
      })
    );
  });
  if (main) {
    setText(doc, 10.8, true);
    const label = String(main.label);
    const negativeTotal = main.label === "Total" && machine.totalAmount < 0;
    const valueLines = doc.splitTextToSize(
      negativeTotal ? a4TotalValue(main.value, machine.totalAmount) : String(main.value ?? "-"),
      width - doc.getTextWidth(label) - 14
    );
    items.push(spacer(3));
    valueLines.forEach((value, index) => {
      items.push({
        height: 15,
        draw(pdf, x, y) {
          setText(pdf, 10.8, true);
          if (index === 0) pdf.text(label, x, y + 10.8);
          if (negativeTotal) pdf.setTextColor(...NEGATIVE_TOTAL);
          pdf.text(value, x + width, y + 10.8, { align: "right" });
          if (negativeTotal) pdf.setTextColor(...INK);
        },
      });
    });
  }
  (machine.noteLines || []).forEach((line) => {
    items.push(...wrappedItems(doc, line, width - 10, { size: 9.2, indent: 10 }));
  });
  items.push(spacer(4), ruleItem(width, 0));
  return items;
};

const summaryItems = (doc, summary, width) => {
  if (summary.percentageMode === "rented_by_type") return [];
  const items = [spacer(4)];
  if (summary.itemCount > 1) {
    items.push(
      ...wrappedItems(doc, `Total ${summary.typeLabel} ${summary.totalAmountLabel}`, width, {
        size: 10.5,
        bold: true,
      })
    );
  }
  const split =
    summary.percentageMode === "single"
      ? `Cliente ${summary.clientPercentageLabel}% ${summary.clientAmountLabel} | Empresa ${summary.ownerPercentageLabel}% ${summary.ownerAmountLabel}`
      : `Cliente ${summary.clientAmountLabel} | Empresa ${summary.ownerAmountLabel}`;
  items.push(...wrappedItems(doc, split, width, { size: 9.7, bold: true }), spacer(2));
  return items;
};

export function renderCollectionA4Pdf(model = {}) {
  const doc = new jsPDF({ orientation: "p", unit: "pt", format: "a4", compress: true });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const width = pageWidth - 2 * MARGIN_X;
  const bottom = pageHeight - MARGIN_BOTTOM;
  const pageCapacity = bottom - MARGIN_TOP;
  let y = MARGIN_TOP;
  let currentSection = null;

  const drawItems = (items) => {
    items.forEach((item) => {
      item.draw(doc, MARGIN_X, y);
      y += item.height;
    });
  };
  const sectionItems = (title, continuation = false) => [
    spacer(7),
    ...wrappedItems(doc, continuation ? `${title} (continuación)` : title, width, {
      size: 12.5,
      bold: true,
    }),
    ruleItem(width),
    spacer(4),
  ];
  const newPage = (continuation = false) => {
    doc.addPage();
    y = MARGIN_TOP;
    if (continuation && currentSection) drawItems(sectionItems(currentSection, true));
  };
  const ensure = (height, continuation = true) => {
    if (y + height > bottom) newPage(continuation);
  };

  const localOwner =
    [model.header?.localName, model.header?.ownerName]
      .map((part) => String(part ?? "").trim())
      .filter(Boolean)
      .join(" | ") || "-";
  const header = [
    ...wrappedItems(doc, localOwner, width, { size: 13, bold: true }),
    ...wrappedItems(doc, `FECHA ${model.header?.dateLabel ?? "-"}`, width, { size: 11 }),
    spacer(7),
    ruleItem(width),
  ];
  drawItems(header);

  const totals = model.generalTotals || {};
  const general = [
    spacer(7),
    ruleItem(width),
    ...wrappedItems(doc, `TOTAL ${totals.totalAmountLabel ?? "-"}`, width, {
      size: 13,
      bold: true,
    }),
    ...wrappedItems(
      doc,
      `Cliente ${totals.clientAmountLabel ?? "-"} | Empresa ${totals.ownerAmountLabel ?? "-"}`,
      width,
      { size: 10.8, bold: true }
    ),
  ];
  const generalHeight = itemHeight(general);

  (model.groups || []).forEach((group, groupIndex) => {
    currentSection = group.title;
    const heading = sectionItems(group.title);
    const entries = group.machines || [];
    const machineWidth = group.type === "Tragamoneda" ? width : width * 0.52;
    const machineBlocks = entries.map((machine) => machineItems(doc, machine, machineWidth));
    const summary = summaryItems(doc, group.summary, width);
    const firstHeight = machineBlocks.length
      ? Math.min(itemHeight(machineBlocks[0]), pageCapacity - itemHeight(heading))
      : 0;
    const finalSectionHeight =
      itemHeight(heading) +
      machineBlocks.reduce((sum, block) => sum + itemHeight(block), 0) +
      itemHeight(summary) +
      generalHeight;
    if (
      groupIndex === model.groups.length - 1 &&
      machineBlocks.length > 1 &&
      finalSectionHeight <= pageCapacity &&
      y + finalSectionHeight > bottom
    ) {
      newPage(false);
    }
    ensure(itemHeight(heading) + firstHeight, false);
    drawItems(heading);

    machineBlocks.forEach((block, index) => {
      const blockHeight = itemHeight(block);
      const last = index === machineBlocks.length - 1;
      const isFinalGroup = groupIndex === model.groups.length - 1;
      const generalReserve = isFinalGroup ? generalHeight : 0;
      const desired = blockHeight + (last ? itemHeight(summary) + generalReserve : 0);
      if (desired <= pageCapacity - itemHeight(sectionItems(group.title, true))) {
        ensure(desired);
      } else if (blockHeight <= pageCapacity - itemHeight(sectionItems(group.title, true))) {
        ensure(blockHeight);
      }

      const oversized = blockHeight > pageCapacity - itemHeight(sectionItems(group.title, true));
      block.forEach((item) => {
        if (oversized && y + item.height > bottom) {
          newPage(true);
          drawItems(
            wrappedItems(doc, `${entries[index].title} (continuación)`, machineWidth, {
              size: 10.8,
              bold: true,
            })
          );
        }
        // Normal blocks were reserved as a unit above.
        item.draw(doc, MARGIN_X, y);
        y += item.height;
      });
    });

    if (summary.length) {
      ensure(itemHeight(summary));
      drawItems(summary);
    }
  });

  currentSection = null;
  ensure(generalHeight, false);
  drawItems(general);

  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    setText(doc, 8.5, false, MUTED);
    doc.text(`${page} / ${pages}`, pageWidth - MARGIN_X, pageHeight - 18, { align: "right" });
  }
  return doc;
}

const shareOrDownload = async (pdf, filename) => {
  const safeName = `${sanitizeFilename(filename).replace(/\.pdf$/i, "")}.pdf`;
  const blob = pdf.output("blob");
  const file = new File([blob], safeName, { type: "application/pdf" });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    await navigator.share({ files: [file], title: safeName });
    return;
  }
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = safeName;
    anchor.click();
  } finally {
    URL.revokeObjectURL(url);
  }
};

export default async function generateCollectionA4Pdf(
  model,
  { filename = "recaudación.pdf" } = {}
) {
  const doc = renderCollectionA4Pdf(model);
  await shareOrDownload(doc, filename);
  return true;
}
